import React,{createContext,useCallback,useContext,useEffect,useState} from "react";
import {useLocation} from "react-router-dom";
import {AuthContext} from "src/context/Auth";
import {builderPreviewPage,pageModeFor} from "src/views/pages/Home/homeLayout";

export const SITE_MODES={CLIENT:"CLIENT",CREATOR:"CREATOR"};

export const SITE_MODE_STORAGE_KEY="siteMode";

/** Individual accounts, or org accounts converted/marked as providers, act as Creators. */
export const isCreatorAccount=(user)=>{
  if(!user)return false;
  if(user.userType==="INDIVIDUAL")return true;
  return user.userType==="ORGANIZATION"&&user.isProvider===true;
};

const STORAGE_KEY=SITE_MODE_STORAGE_KEY;

// Pages that only make sense for one audience switch the mode on their own, so shared links show the right menu.
const ROUTE_MODES={
  "/opportunities":SITE_MODES.CREATOR,
  "/my-applications":SITE_MODES.CREATOR,
  "/display-your-profile":SITE_MODES.CREATOR,
  "/creators":SITE_MODES.CLIENT,
  "/hire-for-me":SITE_MODES.CLIENT,
  "/hire-for-me/job-list":SITE_MODES.CLIENT,
  "/post-opportunity":SITE_MODES.CLIENT,
  "/my-opportunities":SITE_MODES.CLIENT,
  "/opportunity-applicants":SITE_MODES.CLIENT,
};

const ROUTE_PREFIX_MODES=[
  {prefix:"/creators/",mode:SITE_MODES.CLIENT},
  {prefix:"/hire-for-me/",mode:SITE_MODES.CLIENT},
];

const getRouteMode=(pathname)=>
  ROUTE_MODES[pathname]||ROUTE_PREFIX_MODES.find((item)=>pathname.startsWith(item.prefix))?.mode;

const isValidMode=(value)=>Object.values(SITE_MODES).includes(value);

const readStoredMode=()=>{
  const stored=window.localStorage.getItem(STORAGE_KEY);
  return isValidMode(stored)?stored:null;
};

export const SiteModeContext=createContext({
  mode:SITE_MODES.CLIENT,
  setMode:()=>{},
  isClient:true,
  isCreator:false,
});

// Pricing pages accept ?audience=client|creator for shareable links, otherwise they follow the site mode.
export const usePricingAudience=()=>{
  const location=useLocation();
  const {mode}=useContext(SiteModeContext);
  const fromQuery=(new URLSearchParams(location.search).get("audience")||"").toUpperCase();
  return isValidMode(fromQuery)?fromQuery:mode;
};

// Inside the admin page builder iframe the edited page decides the mode; it is never persisted,
// because the iframe shares localStorage with the admin's own visits to the site.
const PREVIEW_PAGE=builderPreviewPage();
const PREVIEW_MODE=PREVIEW_PAGE?pageModeFor(PREVIEW_PAGE):null;

export default function SiteModeProvider(props){
  const location=useLocation();
  const auth=useContext(AuthContext);
  const [storedMode,setStoredMode]=useState(readStoredMode);

  const setMode=useCallback((nextMode)=>{
    if(!isValidMode(nextMode)||PREVIEW_MODE)return;
    window.localStorage.setItem(STORAGE_KEY,nextMode);
    setStoredMode(nextMode);
  },[]);

  const routeMode=getRouteMode(location.pathname);

  useEffect(()=>{
    if(routeMode)setMode(routeMode);
  },[routeMode,setMode]);

  const accountMode=isCreatorAccount(auth?.userData)?SITE_MODES.CREATOR:SITE_MODES.CLIENT;
  const mode=PREVIEW_MODE||routeMode||storedMode||accountMode;

  const value={
    mode,
    setMode,
    isClient:mode===SITE_MODES.CLIENT,
    isCreator:mode===SITE_MODES.CREATOR,
  };

  return(
    <SiteModeContext.Provider value={value}>
      {props.children}
    </SiteModeContext.Provider>
  );
}
